import _ from 'lodash';
import { AssocEntry, EraQlAst } from '../eraql-ast/ast';
import { WithMeta } from '../lex';
import { withNullMeta } from '../lex/types';
import { Ty } from '../../ty';
import { Assert, Freeze } from '../../utils';
import { AST } from '../../ast';

export const fromCallOf = (
  sym: string,
  args: readonly (string | number | boolean | EraQlAst)[],
  display: AST.FunctionCallDisplay
): EraQlAst => ({
  t: 'call',
  args: args
    .map(
      (arg): EraQlAst =>
        typeof arg === 'string' ||
        typeof arg === 'number' ||
        typeof arg === 'boolean'
          ? { t: 'literal', val: arg }
          : arg
    )
    .map((arg) => withNullMeta(arg)),
  display,
  op: fromSymbol(sym),
});

export const fromSymbol = (sym: string): WithMeta<EraQlAst> =>
  withNullMeta({ t: 'symbol', sym });

export const fromLit = (val: string | number | boolean | Date): EraQlAst => ({
  t: 'literal',
  val,
});

export function fromAssoc<T>(
  kvs: { readonly [field: string]: T },
  callback: (val: T, key: string) => EraQlAst,
  opts?: { alphabetize?: boolean }
): EraQlAst {
  const kvsWithNullMeta = _.entries(kvs).map(([key, val]) =>
    withNullMeta(fromAssocKV(key, callback(val, key)))
  );
  return {
    t: 'assoc',
    entries: opts?.alphabetize
      ? _.sortBy(kvsWithNullMeta, ([name, _]) => name)
      : kvsWithNullMeta,
  };
}

export function fromList<T>(
  elems: readonly T[],
  callback: (elem: T) => EraQlAst
): EraQlAst {
  return {
    t: 'list',
    elems: elems.map((scalar) => withNullMeta(callback(scalar))),
  };
}

export function fromScalar(
  val: Ty.Scalar,
  ty: Ty.ExtendedAttributeType
): EraQlAst {
  switch (typeof val) {
    case 'string':
    case 'number':
    case 'boolean':
      return { t: 'literal', val };
    case 'object': {
      if (val === null) {
        return {
          t: 'call',
          op: fromSymbol('null_of'),
          args: [withNullMeta({ t: 'symbol', sym: Ty.displayTy(ty) })],
          display: 'fc',
        };
      }

      if (val instanceof Date) {
        return { t: 'literal', val };
      }

      const scalarType = ty.ty;

      if (Freeze.isReadonlyArray(val)) {
        Assert.assert(scalarType.k === 'array');
        return fromList(val, (elt: Ty.Scalar) => fromScalar(elt, scalarType.t));
      }

      Assert.assert(scalarType.k === 'struct');
      return fromAssoc(val, (elt: Ty.Scalar, key: string) =>
        fromScalar(elt, Assert.assertExists(scalarType.fields[key]))
      );
    }

    default:
      return Assert.unreachable(val);
  }
}

const fromAssocKV = (key: string, val: EraQlAst): AssocEntry => ({
  t: 'assoc-kv',
  key: withNullMeta(key),
  val: withNullMeta(val),
});
